import type { FormErrors, FormField, FormValues } from '../types/form'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export function isVisible(field: FormField, values: FormValues): boolean {
  return !field.showIf || values[field.showIf.field] === field.showIf.equals
}

export function isRequired(field: FormField, values: FormValues): boolean {
  if (field.required) return true
  return Boolean(field.requiredIf && values[field.requiredIf.field] === field.requiredIf.equals)
}

export function validateField(field: FormField, values: FormValues): string | undefined {
  const raw = values[field.name]
  const required = isRequired(field, values)

  if (field.type === 'checkbox') {
    return required && raw !== true ? field.requiredMessage ?? 'Please agree to continue' : undefined
  }

  const value = typeof raw === 'string' ? raw.trim() : ''
  if (!value) {
    if (!required) return undefined
    if (field.requiredMessage) return field.requiredMessage
    if (field.type === 'select' || field.type === 'choice') return 'Choose an option'
    if (field.type === 'tel') return 'Enter your phone number'
    return `Enter your ${field.label.toLowerCase()}`
  }

  if (field.type === 'email' && !EMAIL_RE.test(value)) {
    return 'Enter a valid email address, like name@business.rw'
  }

  if (field.type === 'tel') {
    const digits = value.replace(/\D/g, '')
    const country = (values[`${field.name}__cc`] as string) || 'RW'
    const valid = country === 'RW' ? /^0?7[2389]\d{7}$/.test(digits) : digits.length >= 7 && digits.length <= 12
    if (!valid) return 'Enter a valid phone number'
  }

  if (field.type === 'password' && value.length < 8) {
    return 'Use at least 8 characters'
  }

  return undefined
}

export function validateAll(fields: FormField[], values: FormValues): FormErrors {
  const errors: FormErrors = {}
  fields.forEach((field) => {
    if (!isVisible(field, values)) return
    const error = validateField(field, values)
    if (error) errors[field.name] = error
  })
  return errors
}

/** 0 = empty, 1 = weak, 2 = fair, 3 = strong */
export function passwordStrength(password: string): 0 | 1 | 2 | 3 {
  if (!password) return 0
  let score = 0
  if (password.length >= 8) score += 1
  if (/\d/.test(password) && /[a-zA-Z]/.test(password)) score += 1
  if (/[A-Z]/.test(password) && /[^a-zA-Z0-9]/.test(password) && password.length >= 10) score += 1
  return Math.max(1, score) as 1 | 2 | 3
}
